import React from 'react';
import Divider from '@mui/material/Divider';
import SvgIcon from '@mui/material/SvgIcon';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import SaveIcon from '@mui/icons-material/Save';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import OpacityIcon from '@mui/icons-material/Opacity';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import PaletteIcon from '@mui/icons-material/Palette';
import { ProjectModule } from 'providers/api';
import { i18n } from 'i18n';
import { useProject } from 'modules/project/providers/project';
import { Model } from '../../../hooks/useModelState';
import { useMarquee } from '../../../contexts/MarqueeContext';
import { ISceneManagerContext } from '../../../hooks/SceneManagerProvider/sceneManagerContext';
import StopeLegendBuilderDialog from '../../../Components/StopeLegendBuilder/StopeLegendBuilderDialog';
import { resetRoutesIfStoredRouteShown } from '../../../StopePanelPage/recipeModelUtils';
import { NO_LEVEL_AREA_FILTER } from '../../../MineModel/levelAreaFilter';
import { NO_SPOOL_DATE_FILTER } from '../../../MineModel/spoolDateFilter';
import { applyIsolate, clearIsolate } from '../bulkEditIsolate';
import {
  applyIsolateFilters,
  removeIsolateFilters,
  resetIsolateStage,
  getIsolateStage,
  nextIsolateStage
} from '../../StopeSelectedScene/StopeSelectedSceneActions';
import ToolbarSection from './ToolbarSection';
import ToolbarButton from './ToolbarButton';
import TunnelProfileDisplayControls from './TunnelProfileDisplayControls';
import { splitSelectedIdsByType, useBulkEditSceneState } from './utils';
import { getMeasureState, subscribeToMeasureState, updateMeasureState } from './measureState';

interface GeneralTabProps {
  model: Model;
  zUpMode: boolean;
  onZUpModeChange: (v: boolean) => void;
  context: ISceneManagerContext;
}

const ZUpRotationIcon = (props: React.ComponentProps<typeof SvgIcon>) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M7.1 8.5A5.5 5.5 0 0 1 17 10h-2.5l3.5 3.5L21.5 10H19a7.5 7.5 0 0 0-13.5-2.1L7.1 8.5z" />
    <path d="M16.9 15.5A5.5 5.5 0 0 1 7 14h2.5L6 10.5 2.5 14H5a7.5 7.5 0 0 0 13.5 2.1l-1.6-.6z" />
    <rect x="11.25" y="2" width="1.5" height="20" rx="0.75" fill="currentColor" opacity="0.7" />
  </SvgIcon>
);

const GeneralTab = ({
  model,
  zUpMode,
  onZUpModeChange,
  context,
}: GeneralTabProps) => {
  const [, forceUpdate] = React.useReducer((x: number) => x + 1, 0);
  const [legendBuilderOpen, setLegendBuilderOpen] = React.useState(false);
  const { showPipes } = model;
  const { selectedIds } = useBulkEditSceneState();
  const { state: { enabledModules } } = useProject();
  const marquee = useMarquee();
  const stopeFillEnabled = enabledModules.includes(ProjectModule.StopeFill);
  const { backgroundColour } = getMeasureState();
  const isWhiteBg = backgroundColour === '#ffffff';

  const selectionSplit = splitSelectedIdsByType(model, selectedIds);
  const isSingleStopeSelection = (
    selectionSplit.stopeIds.length === 1
    && selectionSplit.nodeIds.length === 0
    && selectionSplit.spoolIds.length === 0
  );
  const canStartRouteIsolate = isSingleStopeSelection || !!model.selectedStope;
  const routeIsolateStage = getIsolateStage();

  const handleIsolateSelection = () => {
    applyIsolate(model, selectionSplit, marquee.cameraRef.current, marquee.controlsRef.current, marquee.modelTransform);
  };

  const handleIsolateRoute = () => {
    if (isSingleStopeSelection) {
      const [stopeId] = selectionSplit.stopeIds;
      const stope = Object.values(model.stopes).flat().find((s) => s?.data?.stopeId === stopeId);
      if (stope && model.selectedStope?.data?.stopeId !== stopeId) {
        model.setSelectedStope(stope);
      }
    } else if (!model.selectedStope) {
      return;
    }
    nextIsolateStage();
    applyIsolateFilters(model);
    forceUpdate();
  };

  const handleUnisolate = () => {
    clearIsolate(model);
    removeIsolateFilters(model);
    model.setLevelAreaFilter(NO_LEVEL_AREA_FILTER);
    model.setSpoolDateFilter(NO_SPOOL_DATE_FILTER);
    resetIsolateStage();
    forceUpdate();
  };

  const handleTogglePipes = () => model.setSpoolColoursByPipe(!showPipes);
  const handleToggleStopeShapes = () => model.setShowStopeShapes(!model.showStopeShapes);
  const handleToggleInactiveSpools = () => model.setShowInactiveSpools(!model.showInactiveSpools);
  const handleToggleBackground = () => {
    updateMeasureState({ backgroundColour: isWhiteBg ? '#000000' : '#ffffff' });
  };
  const handleToggleZUp = () => onZUpModeChange(!zUpMode);
  const handleLoadView = () => context.actions.loadView();
  const handleSaveView = () => context.actions.saveView();
  const handleOpenAutoRecipe = () => {
    resetRoutesIfStoredRouteShown(model);
    context.actions.setAutoRecipeCreationOpen(true);
  };
  const handleOpenAutoRecipeList = () => context.actions.setAutoRecipeListOpen(true);
  const handleOpenAutoPour = () => context.actions.setAutoPourCreationOpen(true);
  const handleOpenAutoPourList = () => context.actions.setAutoPourListOpen(true);

  let routeLabel = i18n._('Route');
  if (routeIsolateStage === 1) routeLabel = i18n._('All Routes');
  else if (routeIsolateStage === 2) routeLabel = i18n._('Active Route');

  React.useEffect(() => subscribeToMeasureState(() => forceUpdate()), []);

  return (
    <>
      <ToolbarSection label={i18n._('Isolate')}>
        <ToolbarButton
          label={i18n._('Selected')}
          tooltipTitle={i18n._('Isolate Selection')}
          description={i18n._('Hide everything except the currently selected nodes, spools and stopes, and frame the camera on them.')}
          disabledDescription={i18n._('Select nodes, spools or stopes first.')}
          icon={<CenterFocusStrongIcon fontSize="small" />}
          onClick={handleIsolateSelection}
          disabled={selectedIds.length === 0}
        />
        <ToolbarButton
          label={routeLabel}
          tooltipTitle={i18n._('Isolate Route')}
          description={i18n._('Show only the routes feeding the selected stope. Click again to step from all routes to just the active route.')}
          disabledDescription={i18n._('Select a single stope to trace its routes.')}
          icon={<AltRouteIcon fontSize="small" />}
          onClick={handleIsolateRoute}
          disabled={!canStartRouteIsolate && routeIsolateStage === 0}
          selected={routeIsolateStage !== 0}
        />
        <ToolbarButton
          label={i18n._('Show All')}
          tooltipTitle={i18n._('Show All')}
          description={i18n._('Clear any isolation and the level, area and date filters, and show the full reticulation network again.')}
          icon={<VisibilityIcon fontSize="small" />}
          onClick={handleUnisolate}
        />
      </ToolbarSection>
      <Divider orientation="vertical" flexItem />
      <ToolbarSection label={i18n._('Camera')}>
        <ToolbarButton
          label={i18n._('Z-Up')}
          tooltipTitle={i18n._('Z-Up Rotation Lock')}
          description={zUpMode
            ? i18n._('Camera rotation is locked to the vertical Z axis. Click to switch to free orbit.')
            : i18n._('Free 3D orbit camera. Click to lock rotation to the vertical Z axis.')}
          icon={<ZUpRotationIcon fontSize="small" />}
          onClick={handleToggleZUp}
          selected={zUpMode}
        />
        <ToolbarButton
          label={i18n._('Load')}
          tooltipTitle={i18n._('Load Saved View')}
          description={i18n._('Restore a previously saved camera position and zoom for this project.')}
          icon={<OpenInBrowserIcon fontSize="small" />}
          onClick={handleLoadView}
        />
        <ToolbarButton
          label={i18n._('Save')}
          tooltipTitle={i18n._('Save Current View')}
          description={i18n._('Save the current camera position and zoom so you can return to it later.')}
          icon={<SaveIcon fontSize="small" />}
          onClick={handleSaveView}
        />
      </ToolbarSection>
      <Divider orientation="vertical" flexItem />
      <ToolbarSection label={i18n._('Display')}>
        <ToolbarButton
          label={i18n._('Pipes')}
          tooltipTitle={i18n._('Colour by Pipe Type')}
          description={i18n._('Shade spools by pipe type and show a legend of the pipe types in use.')}
          icon={<LinearScaleIcon fontSize="small" />}
          onClick={handleTogglePipes}
          selected={showPipes}
        />
        <ToolbarButton
          label={i18n._('Inactive')}
          tooltipTitle={i18n._('Show Inactive Spools')}
          description={i18n._('Show inactive spools faintly, or hide them. Inactive spools are never used for routing.')}
          icon={model.showInactiveSpools ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
          onClick={handleToggleInactiveSpools}
          selected={model.showInactiveSpools}
        />
        {stopeFillEnabled && (
          <ToolbarButton
            label={i18n._('Stope Shapes')}
            tooltipTitle={i18n._('Toggle Stope Shapes')}
            description={i18n._('Show or hide the imported 3D stope solids in the scene.')}
            icon={<ViewInArIcon fontSize="small" />}
            onClick={handleToggleStopeShapes}
            selected={model.showStopeShapes}
          />
        )}
        <ToolbarButton
          label={i18n._('Background')}
          tooltipTitle={i18n._('Toggle Background')}
          description={i18n._('Switch the 3D scene background between black and white.')}
          icon={isWhiteBg ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          onClick={handleToggleBackground}
        />
        {stopeFillEnabled && (
          <ToolbarButton
            label={i18n._('Legend')}
            tooltipTitle={i18n._('Stope Legend Builder')}
            description={i18n._('Build and save value or range bins for colouring imported stope solids.')}
            icon={<PaletteIcon fontSize="small" />}
            onClick={() => setLegendBuilderOpen(true)}
            selected={!!model.stopeLegendConfig && model.showStopeShapes}
          />
        )}
      </ToolbarSection>
      <Divider orientation="vertical" flexItem />
      <TunnelProfileDisplayControls model={model} />
      <Divider orientation="vertical" flexItem />
      <ToolbarSection label={i18n._('Auto Recipe')}>
        <ToolbarButton
          label={i18n._('Create')}
          tooltipTitle={i18n._('Create Auto Recipe')}
          description={i18n._('Generate paste or hydraulic recipes automatically for the selected stope routes.')}
          icon={<AutoFixHighIcon fontSize="small" />}
          onClick={handleOpenAutoRecipe}
        />
        <ToolbarButton
          label={i18n._('List')}
          tooltipTitle={i18n._('Auto Recipe Results')}
          description={i18n._('Open the list of previously generated auto-recipes and their statuses.')}
          icon={<FactCheckIcon fontSize="small" />}
          onClick={handleOpenAutoRecipeList}
        />
      </ToolbarSection>
      {stopeFillEnabled && (
        <>
          <Divider orientation="vertical" flexItem />
          <ToolbarSection label={i18n._('Auto Pour')}>
            <ToolbarButton
              label={i18n._('Create')}
              tooltipTitle={i18n._('Create Auto Pour')}
              description={i18n._('Create auto pours from stope pour preset groups and schedule the preset sequence.')}
              icon={<OpacityIcon fontSize="small" />}
              onClick={handleOpenAutoPour}
            />
            <ToolbarButton
              label={i18n._('List')}
              tooltipTitle={i18n._('Auto Pour Results')}
              description={i18n._('Open the list of previously generated auto-pours and delete a whole batch at once.')}
              icon={<FactCheckIcon fontSize="small" />}
              onClick={handleOpenAutoPourList}
            />
          </ToolbarSection>
        </>
      )}
      {stopeFillEnabled && legendBuilderOpen && (
        <StopeLegendBuilderDialog
          open={legendBuilderOpen}
          model={model}
          onClose={() => setLegendBuilderOpen(false)}
        />
      )}
    </>
  );
};

export default GeneralTab;
